//  runway.js
//  Daily cash ledger + short-term balance risk.
//
//  buildProjection() answers "what is my balance at the END of each of the
//  next 12 months". That resolution structurally cannot answer "am I about to
//  overdraft", because the failure mode is intra-month:
//
//      Aug  1   balance     $800
//      Aug  5   rent      -$1,500   <- actually negative here
//      Aug 15   paycheck  +$2,000
//      Aug 31   balance   +$1,300   <- month-end looks perfectly healthy
//
//  A month-end series shows green on exactly the month you overdraft. So this
//  module walks DAY BY DAY, expanding each detected series into its real dated
//  occurrences via expandRecurring() rather than collapsing it to
//  occurrences-per-month x mean amount the way projection.js does.
//
//  Two things feed the ledger:
//    1. Recurring occurrences — dated, from the detected series.
//    2. Variable spend        — undated discretionary burn, amortized per-day
//                               from the historical variable-net mean.
//
//  (2) matters more than it looks. A ledger built only from recurring series
//  is systematically OPTIMISTIC: it models rent and payroll but not groceries
//  and gas, so it reports "you're clear" right up until you aren't. A runway
//  feature that cries all-clear and is wrong is worse than no feature, because
//  one bad call is enough for people to stop trusting it.
//
//  Risk is therefore assessed against two lines, ASYMMETRICALLY:
//    - p50 (mean variable burn)          -> used to assert "you're clear"
//    - p10 (pessimistic, mean - z*sigma) -> used to raise a warning
//  A warning fires early, but the all-clear is only claimed when the
//  pessimistic case survives too. Cautious in both directions.
//
//  ACCOUNT LENS: this runs on CASH. Callers pass the cash balance and
//  cash-account transactions. Credit-card *purchases* don't move cash on the
//  purchase date — card *payments* do. That is deliberately the opposite lens
//  from buildProjection's (which drops repayments/transfers so card purchases
//  aren't double-counted against budgets). Using the spending lens here would
//  hide every card payment and make the runway read too rosy.

import { expandRecurring } from './recurrence.js';
import { seriesKeyOf, seriesAmount } from './series-key.js';
import { isMoneyIn } from '../txType.js';
import { varianceFromHistory } from './variance.js';

// Mean Gregorian month. Converts the monthly variance stats from variance.js
// into a per-day rate without drifting a day or two per month.
const DAYS_PER_MONTH = 30.436875;

// 80% interval, matching variance.js's band convention.
const Z_80 = 1.2815515655446004;

// Default low-balance floor. Not 0: hitting exactly zero is already too late to
// react to, and most people want warning while there's still room to move money.
export const DEFAULT_RUNWAY_FLOOR = 100;

// 'YYYY-MM-DD' is parsed as LOCAL midnight, not UTC — parsing it bare puts it
// at UTC midnight, which lands on the previous day in negative-UTC zones and
// silently shifts every event by one day.
function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(value) {
  const d = parseDate(value);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(date, n) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + n);
  return d;
}

// Local 'YYYY-MM-DD'. en-CA is already the codebase's idiom for this
// (see utils/dates.js toInputDate) and avoids a UTC round-trip.
function dateKey(date) {
  return date.toLocaleDateString('en-CA');
}

const round2 = (n) => parseFloat(n.toFixed(2));

// Groups recurring transactions into series, carrying the fields
// expandRecurring() needs: the latest instance's date + cadence (the anchor
// it projects forward from) and the series' mean per-occurrence amount.
//
// Anchoring on the LATEST instance is what keeps already-happened occurrences
// out of the ledger: they're baked into the current balance already, and
// expandRecurring starts at the occurrence *after* lastDate.
export function recurringSeries(transactions) {
  const map = {};
  (transactions || [])
    .filter((t) => t && t.isRecurring)
    .forEach((t) => {
      const d = parseDate(t.date);
      if (!d) return;
      const key = seriesKeyOf(t);
      if (!map[key]) {
        map[key] = {
          key,
          description: t.description,
          type: t.type,
          instances: [],
          lastDate: d,
          recurrenceType: t.recurrenceType,
          endDate: t.endDate || null,
        };
      }
      const s = map[key];
      s.instances.push(t);
      if (d > s.lastDate) {
        s.lastDate = d;
        s.recurrenceType = t.recurrenceType;
        s.endDate = t.endDate || null;
      }
    });

  return Object.values(map).map((s) => ({
    key: s.key,
    description: s.description,
    type: s.type,
    recurrenceType: s.recurrenceType,
    lastDate: s.lastDate,
    endDate: s.endDate,
    amount: seriesAmount(s.instances),
  }));
}

// Every dated recurring occurrence in (from, to], signed as a balance delta.
export function recurringEvents(transactions, from, to) {
  const out = [];
  recurringSeries(transactions).forEach((s) => {
    if (!s.recurrenceType) return;
    const dates = expandRecurring(
      { lastDate: s.lastDate, recurrenceType: s.recurrenceType, endDate: s.endDate },
      from,
      to,
    );
    dates.forEach((d) => {
      out.push({
        date: startOfDay(d),
        seriesKey: s.key,
        description: s.description,
        type: s.type,
        amount: round2(s.amount),
        delta: round2(isMoneyIn(s.type) ? s.amount : -s.amount),
      });
    });
  });
  return out;
}

/**
 * Day-by-day cash balance over the horizon.
 *
 * Index 0 is today's anchor (balance = startBalance, no events); each
 * subsequent entry applies that day's recurring occurrences plus the
 * amortized variable burn.
 *
 * @returns {Array<{date: Date, key: string, balance: number, pessimistic: number, events: Array}>}
 */
export function buildDailyLedger(startBalance, transactions, options = {}) {
  const {
    horizonDays = 60,
    today = new Date(),
    variance = null,
    minSamples = 3,
    z = Z_80,
    extraEvents = [],
    excludeSeriesKeys = null,
  } = options;

  const start = startOfDay(today) || startOfDay(new Date());
  const last = addDays(start, horizonDays);
  let events = recurringEvents(transactions, addDays(start, 1), last);

  // Series another projector has taken ownership of — currently card payments
  // sized from the card's live balance (see card-payments.js). Their generic
  // mean-based occurrences are dropped here so the payment isn't counted twice.
  if (excludeSeriesKeys && excludeSeriesKeys.size > 0) {
    events = events.filter((e) => !excludeSeriesKeys.has(e.seriesKey));
  }

  (extraEvents || []).forEach((e) => {
    const d = startOfDay(e.date);
    if (!d || d > last) return;
    // Day 0 is the anchor: startBalance already reflects everything that has
    // posted, and the loop below never visits it. An event dated today or
    // earlier hasn't posted yet (that's why it's projected), so the soonest it
    // can land is the first future day — dropping it would lose a debit we
    // specifically know is coming.
    events.push({ ...e, date: d > start ? d : addDays(start, 1) });
  });

  const byDay = {};
  events.forEach((e) => {
    (byDay[dateKey(e.date)] ||= []).push(e);
  });

  // Historical variable net is typically negative (discretionary spend). Its
  // mean is the p50 burn; its stddev drives the pessimistic line.
  const monthlyMean = variance && Number.isFinite(variance.mean) ? variance.mean : 0;
  const dailyBurn = monthlyMean / DAYS_PER_MONTH;

  // Same guard as projectionBands: with 1-2 noisy samples the band is
  // meaningless, so collapse it to p50 rather than invent a scary wedge.
  const reliable =
    !!variance &&
    variance.sampleSize >= minSamples &&
    Number.isFinite(variance.stddev) &&
    variance.stddev > 0;

  const days = [
    {
      date: start,
      key: dateKey(start),
      balance: round2(startBalance),
      pessimistic: round2(startBalance),
      events: [],
    },
  ];

  let balance = startBalance;
  for (let i = 1; i <= horizonDays; i++) {
    const date = addDays(start, i);
    const key = dateKey(date);
    const dayEvents = byDay[key] || [];
    balance += dayEvents.reduce((s, e) => s + e.delta, 0) + dailyBurn;

    // sigma grows with sqrt(elapsed months) — random-walk propagation, the
    // same model variance.js uses month-over-month.
    const sigma = reliable ? z * variance.stddev * Math.sqrt(i / DAYS_PER_MONTH) : 0;

    days.push({
      date,
      key,
      balance: round2(balance),
      pessimistic: round2(balance - sigma),
      events: dayEvents,
    });
  }

  return days;
}

/**
 * Short-term balance risk assessment.
 *
 * status:
 *   'clear'      — neither line crosses the floor over the horizon.
 *   'at_risk'    — the pessimistic line crosses but the expected line doesn't.
 *   'shortfall'  — the expected line crosses. This is a real projected problem.
 *
 * @returns {{
 *   status: 'clear'|'at_risk'|'shortfall',
 *   floor: number,
 *   days: Array,
 *   breach: null | {date: Date, key: string, balance: number, shortfall: number, cause: object|null},
 *   safeThrough: Date|null,
 *   minBalance: {date: Date, balance: number},
 *   billsCovered: number,
 *   lowConfidence: boolean,
 * }}
 */
export function assessRunway(startBalance, transactions, options = {}) {
  const {
    floor = 0,
    horizonDays = 60,
    today = new Date(),
    lookbackMonths = 6,
    minSamples = 3,
    extraEvents = [],
    excludeSeriesKeys = null,
  } = options;

  const variance =
    options.variance || varianceFromHistory(transactions, { lookbackMonths, today });

  const days = buildDailyLedger(startBalance, transactions, {
    horizonDays,
    today,
    variance,
    minSamples,
    extraEvents,
    excludeSeriesKeys,
  });

  const lowConfidence = !(
    variance.sampleSize >= minSamples &&
    Number.isFinite(variance.stddev) &&
    variance.stddev > 0
  );

  const expectedBreach = days.find((d) => d.balance < floor) || null;
  const pessimisticBreach = days.find((d) => d.pessimistic < floor) || null;

  let status = 'clear';
  if (expectedBreach) status = 'shortfall';
  else if (pessimisticBreach && !lowConfidence) status = 'at_risk';

  const breachDay = expectedBreach || (status === 'at_risk' ? pessimisticBreach : null);

  // The event that actually tipped it under — the largest outflow on the
  // breach day. This is the only part of the warning the user can act on.
  let breach = null;
  if (breachDay) {
    const cause = breachDay.events
      .filter((e) => e.delta < 0)
      .sort((a, b) => a.delta - b.delta)[0] || null;
    const level = expectedBreach ? breachDay.balance : breachDay.pessimistic;
    breach = {
      date: breachDay.date,
      key: breachDay.key,
      balance: level,
      shortfall: round2(floor - level),
      cause,
    };
  }

  // Last day before trouble. With no breach, the full horizon is safe.
  const safeThrough = breachDay
    ? addDays(breachDay.date, -1)
    : days[days.length - 1].date;

  const minBalance = days.reduce(
    (lo, d) => (d.balance < lo.balance ? { date: d.date, balance: d.balance } : lo),
    { date: days[0].date, balance: days[0].balance },
  );

  // Recurring outflows that clear before trouble starts — the "you're covered
  // for the next N bills" signal.
  const billsCovered = days
    .filter((d) => !breachDay || d.date < breachDay.date)
    .reduce((n, d) => n + d.events.filter((e) => e.delta < 0).length, 0);

  return {
    status,
    floor,
    days,
    breach,
    safeThrough,
    minBalance,
    billsCovered,
    lowConfidence,
  };
}

export const _constants = { DAYS_PER_MONTH, Z_80 };
